import * as React from 'react';
import { useSelector } from 'react-redux';
import { ActionAfterViewed } from '@cian/action-after-viewed-component';
import { sendKpnGuideLinkClick, sendKpnGuideLinkShowed } from './analytics/analytics';
import { AnimatedContainer } from '../../components/AnimatedContainer';
import {
  NewbuildingGuideItem,
  QuestionsAndAnswers,
  QuestionsAndAnswersItem,
} from '../../components/LandingPageV2/QuestionsAndAnswers';
import { selectFlightEnabled } from '../../selectors/meta';
import { selectIsAnimationAvailable } from '../../selectors/pageState';

interface IQuestionAnswerItem {
  type: 'service' | 'consultant' | 'support' | 'partial' | 'existing' | 'safety';
  title: string;
  description: string;
}

const DEFAULT_CONTENT: IQuestionAnswerItem[] = [
  {
    type: 'service',
    title: 'Чем вы можете помочь и сколько это стоит?',
    description:
      'Мы помогаем подобрать квартиру, забронировать её и подготовиться к сделке — для пользователей Циана это бесплатно. Некоторые услуги застройщика могут быть платными — например, бронирование. Мы заранее об этом предупредим.',
  },
  {
    type: 'consultant',
    title: 'Кто будет делать подборку квартир?',
    description:
      'С вами будет работать один из наших консультантов — это настоящие гуру поиска жилья, которые отсмотрят за вас тысячи объявлений и найдут тот самый вариант.',
  },
  {
    type: 'support',
    title: 'А кто будет сопровождать до сделки? И что туда входит?',
    description:
      'Наш специалист по новостройкам — консультант передаст ему всю информацию по квартире, которая вам понравилась. Специалист посоветует подходящую ипотечную программу, поможет с бронированием и подскажет, какие документы понадобятся.',
  },
  {
    type: 'partial',
    title: 'Можно заказать только подборку квартир, без сопровождения до сделки?',
    description:
      'Да, без проблем. Если передумаете и захотите, чтобы мы помогли вам с бронированием и документами, — просто скажите об этом своему консультанту.',
  },
  {
    type: 'existing',
    title: 'А наоборот — если я уже знаю, какую квартиру хочу, и мне нужно только сопровождение?',
    description:
      'Можно, если вы нашли объявление на Циане и на нём есть кнопка «Заявка на бронирование»: нажмите на неё, заполните короткую заявку и ждите звонка от нашего специалиста.',
  },
];

const FLIGHT_CONTENT: IQuestionAnswerItem[] = [
  {
    type: 'service',
    title: 'Чем\u00A0вы\u00A0можете помочь и\u00A0сколько это\u00A0стоит?',
    description:
      'Мы\u00A0подбираем квартиру по\u00A0вашим\u00A0пожеланиям, проверяем застройщика, находим выгодные акции, проверяем договор и\u00A0вместе готовимся к\u00A0сделке — для\u00A0пользователей Циана это\u00A0бесплатно. Некоторые услуги\u00A0застройщика могут быть платными\u00A0— например, бронирование. Мы\u00A0заранее об этом предупредим.',
  },
  {
    type: 'partial',
    title: 'Можно заказать только\u00A0подборку квартир, без\u00A0сопровождения до\u00A0сделки?',
    description:
      'Да, без\u00A0проблем. Если\u00A0передумаете и\u00A0захотите, чтобы\u00A0мы\u00A0вместе прошли весь путь до\u00A0подписания договора,\u00A0— просто\u00A0скажите об\u00A0этом своему\u00A0консультанту.',
  },
  {
    type: 'support',
    title:
      'А\u00A0наоборот\u00A0— если\u00A0я\u00A0уже\u00A0знаю, какую квартиру хочу, и\u00A0мне\u00A0нужно только сопровождение?',
    description:
      'Можно, если\u00A0вы\u00A0нашли объявление на\u00A0Циане и\u00A0на\u00A0нём\u00A0есть\u00A0кнопка «Заявка на\u00A0бронирование»: нажмите на\u00A0неё, заполните короткую заявку и\u00A0ждите звонка от\u00A0нашего консультанта.',
  },
  {
    type: 'safety',
    title: 'Покупать новостройку\u00A0— это вообще безопасно?',
    description:
      'Сейчас покупать первичку гораздо\u00A0спокойнее, чем\u00A0ещё 10–12\u00A0лет назад\u00A0— скажем спасибо эскроу-счетам, на\u00A0которых деньги\u00A0хранятся вплоть до\u00A0сдачи дома. Остаются здоровые переживания о\u00A0качестве строительства и\u00A0сроках. Тут\u00A0и\u00A0помогут наши\u00A0консультанты: проверят репутацию застройщика, изучат предыдущие проекты и\u00A0проверят документацию.',
  },
];

export const QuestionsAndAnswersContainer = () => {
  const isAnimationAvailable = useSelector(selectIsAnimationAvailable);
  const flightEnabled = useSelector(selectFlightEnabled);
  const content = flightEnabled ? FLIGHT_CONTENT : DEFAULT_CONTENT;

  return (
    <QuestionsAndAnswers isCenteredTitle={flightEnabled}>
      {content.map(item => (
        <AnimatedContainer key={item.title} isAnimationAvailable={isAnimationAvailable}>
          <QuestionsAndAnswersItem
            flightEnabled={flightEnabled}
            key={item.title}
            type={item.type}
            question={item.title}
            answer={item.description}
          />
        </AnimatedContainer>
      ))}
      {flightEnabled && (
        <AnimatedContainer key="newbuilding-guide" isAnimationAvailable={isAnimationAvailable}>
          <ActionAfterViewed percentVisible={0.8} callback={sendKpnGuideLinkShowed}>
            <NewbuildingGuideItem onLinkClick={sendKpnGuideLinkClick} />
          </ActionAfterViewed>
        </AnimatedContainer>
      )}
    </QuestionsAndAnswers>
  );
};
